import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, X } from 'lucide-react';
import { 
  getParticipants, 
  createParticipant, 
  updateParticipant, 
  deleteParticipant 
} from '../lib/services/participantsService';
import { Participant } from '../types/shared-expenses';

const Participants: React.FC = () => {
  const navigate = useNavigate();
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingParticipant, setEditingParticipant] = useState<Participant | null>(null);
  const [newParticipantName, setNewParticipantName] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadParticipants();
  }, []);

  const loadParticipants = async () => {
    setLoading(true);
    try {
      const { data, error } = await getParticipants();
      
      if (error) {
        throw error;
      }
      
      if (data) {
        setParticipants(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des participants:', error);
      alert('Impossible de charger la liste des participants.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddParticipant = async () => {
    if (!newParticipantName.trim()) {
      alert('Veuillez saisir un nom de participant.');
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await createParticipant(newParticipantName.trim());
      
      if (error) throw error;
      
      if (data) {
        setParticipants(prev => [...prev, data]);
        setNewParticipantName('');
        setShowAddForm(false);
      }
    } catch (error) {
      console.error('Erreur lors de la création du participant:', error);
      alert('Impossible de créer le participant.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateParticipant = async () => {
    if (!editingParticipant || !editingParticipant.nom.trim()) {
      alert('Veuillez saisir un nom de participant.');
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await updateParticipant(editingParticipant.id, editingParticipant.nom.trim());
      
      if (error) throw error;
      
      if (data) {
        setParticipants(prev => prev.map(p => p.id === data.id ? data : p));
        setEditingParticipant(null);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du participant:', error);
      alert('Impossible de mettre à jour le participant.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteParticipant = async (id: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce participant ? Toutes ses participations aux dépenses partagées seront également supprimées.')) {
      return;
    }

    try {
      const { error } = await deleteParticipant(id);
      if (error) throw error;
      
      setParticipants(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      console.error('Erreur lors de la suppression du participant:', error);
      alert('Erreur lors de la suppression du participant');
    }
  };

  const startEditing = (participant: Participant) => {
    setEditingParticipant({ ...participant });
  };

  const cancelEditing = () => {
    setEditingParticipant(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-dark-300">Chargement des participants...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/shared-expenses')}
            className="p-2 text-dark-300 hover:text-white hover:bg-dark-800 rounded-lg transition-colors duration-200"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">Participants</h1>
            <p className="text-dark-300 mt-2">Gérez les participants à vos dépenses partagées</p>
          </div>
        </div>
        
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center space-x-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors duration-200"
        >
          <Plus className="w-5 h-5" />
          <span>Ajouter un participant</span>
        </button>
      </div>

      {/* Formulaire d'ajout */}
      {showAddForm && (
        <div className="bg-dark-900 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">Nouveau participant</h2>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewParticipantName('');
              }}
              className="p-2 text-dark-300 hover:text-white hover:bg-dark-800 rounded-lg transition-colors duration-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex items-center space-x-4">
            <input
              type="text"
              value={newParticipantName}
              onChange={(e) => setNewParticipantName(e.target.value)}
              placeholder="Nom du participant"
              className="flex-1 px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              onKeyPress={(e) => e.key === 'Enter' && handleAddParticipant()}
            />
            <button
              onClick={handleAddParticipant}
              disabled={saving || !newParticipantName.trim()}
              className="px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-dark-700 text-white rounded-lg font-medium transition-colors duration-200"
            >
              {saving ? 'Ajout...' : 'Ajouter'}
            </button>
          </div>
        </div>
      )}

      {/* Liste des participants */}
      {participants.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-dark-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <Plus className="w-10 h-10 text-dark-400" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">Aucun participant</h3>
          <p className="text-dark-300 mb-6">
            Ajoutez votre premier participant pour commencer à créer des dépenses partagées.
          </p>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors duration-200"
          >
            Ajouter un participant
          </button>
        </div>
      ) : (
        <div className="bg-dark-900 rounded-lg overflow-hidden">
          <div className="p-6 border-b border-dark-800">
            <h2 className="text-xl font-semibold text-white">
              Liste des participants ({participants.length})
            </h2>
          </div>
          
          <div className="divide-y divide-dark-800">
            {participants.map((participant) => (
              <div key={participant.id} className="p-6">
                {editingParticipant?.id === participant.id ? (
                  // Mode édition
                  <div className="flex items-center space-x-4">
                    <input
                      type="text"
                      value={editingParticipant.nom}
                      onChange={(e) => setEditingParticipant(prev => prev ? { ...prev, nom: e.target.value } : null)}
                      className="flex-1 px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      onKeyPress={(e) => e.key === 'Enter' && handleUpdateParticipant()}
                    />
                    <button
                      onClick={handleUpdateParticipant}
                      disabled={saving || !editingParticipant.nom.trim()}
                      className="px-4 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-dark-700 text-white rounded-lg font-medium transition-colors duration-200"
                    >
                      {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                    </button>
                    <button
                      onClick={cancelEditing}
                      className="px-4 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-lg font-medium transition-colors duration-200"
                    >
                      Annuler
                    </button>
                  </div>
                ) : (
                  // Mode affichage
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-white">{participant.nom}</h3>
                      <p className="text-sm text-dark-400">
                        Créé le {new Date(participant.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => startEditing(participant)}
                        className="p-2 text-dark-300 hover:text-primary-500 hover:bg-dark-800 rounded-lg transition-colors duration-200"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteParticipant(participant.id)}
                        className="p-2 text-dark-300 hover:text-red-500 hover:bg-dark-800 rounded-lg transition-colors duration-200"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Participants; 